import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { products, categories } from '../data/mockData';
import { customizationNote, priceNote, testCountNote } from '../data/serviceContent';
import { PanelCard } from './PanelCard';

const CompareModal = lazy(() => import('./CompareModal').then((module) => ({ default: module.CompareModal })));

const CategoryFilter = ({ filter, onChange }) => (
  <div className="flex flex-wrap gap-2 my-7" role="group" aria-label="Filter panel options" data-testid="category-filter">
    {categories.map((category) => <button key={category.id} type="button" className="hbk-filter" aria-pressed={filter === category.id} onClick={() => onChange(category.id)} data-testid={`filter-${category.id}`}>
      {category.label} <span>({category.id === 'all' ? products.length : products.filter((panel) => panel.category === category.id).length})</span>
    </button>)}
  </div>
);

const CompareBar = ({ panels, onRemove, onClear, onOpen }) => (
  <div className="hbk-compare-bar" data-testid="compare-bar">
    <div className="flex flex-wrap gap-3 justify-between items-center">
      <p className="text-sm font-semibold" role="status" data-testid="compare-selection-status">{panels.length} of 2 panels selected</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={onClear} className="hbk-compare-clear" data-testid="compare-clear-btn">Clear</button>
        <button onClick={onOpen} disabled={panels.length !== 2} className="btn-cta disabled:opacity-60" data-testid="compare-open-btn">Compare Panels</button>
      </div>
    </div>
    <ul className="grid sm:grid-cols-2 gap-2 mt-2">
      {panels.map((panel) => <li key={panel.id} className="flex items-center justify-between gap-2 min-w-0 text-sm" data-testid={`compare-selected-${panel.id}`}><span className="truncate">{panel.name}</span><button className="min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label={`Remove ${panel.name} from comparison`} onClick={() => onRemove(panel.id)} data-testid={`compare-bar-remove-${panel.id}`}><X size={18} /></button></li>)}
    </ul>
  </div>
);

export default function ProductGrid() {
  const [filter, setFilter] = useState('all');
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const { search } = useLocation();
  const panels = compareIds.map((id) => products.find((panel) => panel.id === id));
  const visible = products.filter((panel) => filter === 'all' || panel.category === filter);
  const toggleCompare = (id) => setCompareIds((previous) => previous.includes(id) ? previous.filter((value) => value !== id) : previous.length < 2 ? [...previous, id] : previous);
  useEffect(() => {
    const ids = [...new Set((new URLSearchParams(search).get('compare') || '').split(','))].filter((id) => products.some((panel) => panel.id === id)).slice(0, 2);
    if (ids.length === 2) { setCompareIds(ids); setCompareOpen(true); }
  }, [search]);
  useEffect(() => {
    document.body.classList.toggle('compare-active', compareIds.length > 0);
    return () => document.body.classList.remove('compare-active');
  }, [compareIds.length]);
  return (
    <section id="shop" className="hbk-section bg-white" data-testid="product-grid-section">
      <div className="max-w-7xl mx-auto px-4">
        <p className="section-marker" data-testid="catalog-label">FIND YOUR STARTING POINT</p>
        <h2 className="section-heading" data-testid="catalog-heading">Explore {products.length} At-Home Panels</h2>
        <p className="text-base max-w-3xl mt-5" data-testid="catalog-description">{customizationNote}</p>
        <details className="hbk-catalog-notes" data-testid="catalog-notes">
          <summary className="hbk-detail-summary" data-testid="catalog-notes-toggle">About panel selection, tests and collection</summary>
          <p className="text-sm text-[#2a3950] mb-3" data-testid="catalog-pricing-note">{priceNote}</p>
          <p className="text-sm text-[#2a3950] mb-4" data-testid="catalog-count-note">{testCountNote} The team confirms your collection requirements.</p>
        </details>
        <CategoryFilter filter={filter} onChange={setFilter} />
        <p className="text-sm mb-5" role="status" data-testid="catalog-filter-status">Showing {visible.length} of {products.length} panel options</p>
        {panels.length > 0 && <CompareBar panels={panels} onRemove={toggleCompare} onClear={() => { setCompareIds([]); setCompareOpen(false); }} onOpen={() => setCompareOpen(true)} />}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="product-grid">
          {visible.map((panel) => <PanelCard key={panel.id} panel={panel} selected={compareIds.includes(panel.id)} compareFull={compareIds.length === 2} onCompare={toggleCompare} />)}
        </div>
        {compareOpen && <Suspense fallback={<p role="status" data-testid="compare-loading">Loading comparison…</p>}><CompareModal panels={panels} open={compareOpen} onOpenChange={setCompareOpen} /></Suspense>}
      </div>
    </section>
  );
}